import { normalizeMessageFilters } from './messageTriggerFilterConfig';

/**
 * Nós do Flow Builder vindos do ManyChat: gatilho de comentário do Instagram,
 * resposta a story, resposta pública, sorteio A/B, botões como saídas do nó
 * «Enviar mensagem», botões de URL e espera em segundos.
 *
 * Backend: ConversationWorkflows::InstagramCommentTriggerFilters,
 * InstagramFirstReply, ReplyResumer, RandomSplit e ActionExecutor.
 */

export const INSTAGRAM_TRIGGER_KINDS = [
  'instagram_comment',
  'instagram_story_reply',
];

/**
 * Redes em que o gatilho de comentário responde (D30). Sem escolha vale só o
 * Instagram: fluxo antigo não passa a responder no Facebook sozinho.
 * Backend: InstagramCommentTriggerFilters.platforms.
 */
export const COMMENT_PLATFORMS = ['instagram', 'facebook'];
export const DEFAULT_COMMENT_PLATFORMS = ['instagram'];

export const normalizeCommentPlatforms = raw => {
  const list = (Array.isArray(raw) ? raw : []).map(p =>
    String(p ?? '')
      .trim()
      .toLowerCase()
  );
  const chosen = COMMENT_PLATFORMS.filter(p => list.includes(p));
  return chosen.length ? chosen : [...DEFAULT_COMMENT_PLATFORMS];
};

/** Liga ou desliga uma rede, sem deixar o gatilho sem nenhuma. */
export const toggleCommentPlatform = (current, platform, checked) => {
  const list = normalizeCommentPlatforms(current);
  const next = COMMENT_PLATFORMS.filter(p =>
    p === platform ? checked : list.includes(p)
  );
  return next.length ? next : list;
};

/**
 * Gatilho que responde no Facebook sem texto de DM: sem a resposta privada a
 * Meta não devolve o contato do Messenger, então só a resposta pública sai e
 * os nós que pedem conversa são pulados.
 * Backend: FacebookCommentDispatcher#reply_publicly_only.
 */
export const facebookRepliesOnlyInPublic = data => {
  const src = data && typeof data === 'object' ? data : {};
  const platforms = normalizeCommentPlatforms(src.comment_filters?.platforms);
  const dmText = String(src.first_reply?.text ?? '').trim();
  return platforms.includes('facebook') && !dmText;
};

export const MAX_URL_BUTTONS = 3;
export const URL_BUTTON_TITLE_MAX = 20;

export const newNodeItemId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `i-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const cleanString = raw => (typeof raw === 'string' ? raw : '').trim();

/** «972, quero, link» → ['972', 'quero', 'link'] (também aceita lista). */
export const keywordsFromInput = raw => {
  const list = Array.isArray(raw) ? raw : String(raw ?? '').split(',');
  const seen = new Set();
  return list
    .map(k => String(k ?? '').trim())
    .filter(k => {
      if (!k) return false;
      const key = k.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const keywordsToInput = list =>
  Array.isArray(list) ? list.join(', ') : '';

export function defaultCommentFilters() {
  return {
    media_id: '',
    keywords: [],
    exclude_keywords: [],
    top_level_only: false,
    live_comments: false,
    platforms: [...DEFAULT_COMMENT_PLATFORMS],
  };
}

export function normalizeCommentFilters(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    media_id: cleanString(src.media_id),
    keywords: keywordsFromInput(src.keywords),
    exclude_keywords: keywordsFromInput(src.exclude_keywords),
    top_level_only: src.top_level_only === true,
    live_comments: src.live_comments === true,
    platforms: normalizeCommentPlatforms(src.platforms),
  };
}

export function normalizeFirstReply(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    text: typeof src.text === 'string' ? src.text : '',
    button_title: cleanString(src.button_title).slice(0, URL_BUTTON_TITLE_MAX),
    link: cleanString(src.link),
  };
}

export function normalizeInstagramCommentTriggerData(d = {}) {
  return {
    comment_filters: normalizeCommentFilters(d.comment_filters),
    first_reply: normalizeFirstReply(d.first_reply),
  };
}

/** Resposta a story: só mensagens recebidas; texto e caixa como no gatilho de mensagem. */
export function normalizeInstagramStoryReplyTriggerData(d = {}) {
  const filters = normalizeMessageFilters(d.message_filters || {});
  filters.message_types = ['incoming'];
  return { message_filters: filters };
}

export const textListFromRaw = raw =>
  Array.isArray(raw) ? raw.map(t => (typeof t === 'string' ? t : '')) : [];

export function normalizeInstagramPublicReplyData(d = {}) {
  const texts = textListFromRaw(d.public_reply_texts);
  if (!texts.length && typeof d.public_reply_text === 'string') {
    texts.push(d.public_reply_text);
  }
  return { public_reply_texts: texts.length ? texts : [''] };
}

/** Botões de resposta rápida: string antiga (só título) ou { id, title }. */
export function normalizeSendMessageButtons(raw) {
  return (Array.isArray(raw) ? raw : [])
    .map(button => {
      if (typeof button === 'string') {
        return { id: newNodeItemId(), title: button };
      }
      if (!button || typeof button !== 'object') return null;
      const id = cleanString(String(button.id ?? '')) || newNodeItemId();
      return {
        id,
        title: typeof button.title === 'string' ? button.title : '',
      };
    })
    .filter(Boolean)
    .slice(0, 13);
}

export const createSendMessageButton = () => ({
  id: newNodeItemId(),
  title: '',
});

export function normalizeUrlButtons(raw) {
  return (Array.isArray(raw) ? raw : [])
    .filter(button => button && typeof button === 'object')
    .map(button => ({
      title: (typeof button.title === 'string' ? button.title : '').slice(
        0,
        URL_BUTTON_TITLE_MAX
      ),
      url: typeof button.url === 'string' ? button.url : '',
    }))
    .slice(0, MAX_URL_BUTTONS);
}

export const createUrlButton = () => ({ title: '', url: '' });

export function normalizeSendMessageNodeData(d = {}) {
  return {
    message_content:
      typeof d.message_content === 'string' ? d.message_content : '',
    buttons: normalizeSendMessageButtons(d.buttons),
    url_buttons: normalizeUrlButtons(d.url_buttons),
  };
}

export const createSplitRule = (weight = 50) => ({
  id: newNodeItemId(),
  weight,
});

export function normalizeRandomSplitData(d = {}) {
  const rules = (Array.isArray(d.split_rules) ? d.split_rules : [])
    .filter(rule => rule && typeof rule === 'object')
    .map(rule => {
      const weight = Number(rule.weight);
      return {
        id: cleanString(String(rule.id ?? '')) || newNodeItemId(),
        weight: Number.isFinite(weight) && weight >= 0 ? Math.round(weight) : 0,
      };
    });
  return {
    split_rules: rules.length
      ? rules
      : [createSplitRule(50), createSplitRule(50)],
  };
}

export const splitRulePercent = (rules, rule) => {
  const total = (rules || []).reduce(
    (sum, r) => sum + (Number(r.weight) || 0),
    0
  );
  if (!total) return 0;
  return Math.round(((Number(rule.weight) || 0) / total) * 100);
};

export const WAIT_UNIT_OPTIONS = [
  { value: 'seconds', label: 'segundos', seconds: 1 },
  { value: 'minutes', label: 'minutos', seconds: 60 },
  { value: 'hours', label: 'horas', seconds: 3600 },
  { value: 'days', label: 'dias', seconds: 86400 },
];

const unitSeconds = unit =>
  WAIT_UNIT_OPTIONS.find(o => o.value === unit)?.seconds || 60;

/**
 * Nó «Esperar». Formato novo: wait_value + wait_unit (+ wait_seconds calculado,
 * que é o que o backend lê). Formato antigo: só wait_minutes.
 */
export function normalizeWaitNodeData(d = {}) {
  let unit = WAIT_UNIT_OPTIONS.some(o => o.value === d.wait_unit)
    ? d.wait_unit
    : null;
  let value = Number(d.wait_value);
  if (!unit || !Number.isFinite(value)) {
    const seconds = Number(d.wait_seconds);
    const minutes = Number(d.wait_minutes);
    if (Number.isFinite(seconds) && seconds > 0) {
      unit = seconds % 60 === 0 ? 'minutes' : 'seconds';
      value = unit === 'minutes' ? seconds / 60 : seconds;
    } else {
      unit = 'minutes';
      value = Number.isFinite(minutes) && minutes > 0 ? minutes : 5;
    }
  }
  value = Math.max(1, Math.round(value));
  const waitSeconds = value * unitSeconds(unit);
  return {
    wait_value: value,
    wait_unit: unit,
    wait_seconds: waitSeconds,
    wait_minutes: Math.max(1, Math.ceil(waitSeconds / 60)),
  };
}

export const summarizeWaitNode = flowNode => {
  if (!flowNode || flowNode.type !== 'action' || flowNode.data?.kind !== 'wait')
    return '';
  const { wait_value: value, wait_unit: unit } = normalizeWaitNodeData(
    flowNode.data
  );
  const option = WAIT_UNIT_OPTIONS.find(o => o.value === unit);
  const label = option ? option.label : unit;
  const singular = {
    segundos: 'segundo',
    minutos: 'minuto',
    horas: 'hora',
    dias: 'dia',
  };
  return `${value} ${value === 1 ? singular[label] || label : label}`;
};

export const summarizeInstagramCommentTrigger = flowNode => {
  if (
    !flowNode ||
    flowNode.type !== 'trigger' ||
    flowNode.data?.kind !== 'instagram_comment'
  )
    return null;
  const filters = normalizeCommentFilters(flowNode.data?.comment_filters);
  const reply = normalizeFirstReply(flowNode.data?.first_reply);
  const keywords = filters.keywords.length
    ? filters.keywords.join(', ')
    : 'Qualquer comentário';
  const scope = filters.media_id ? 'Post escolhido' : 'Qualquer post';
  const text = reply.text.trim();
  const replySnippet = text.length > 44 ? `${text.slice(0, 44)}…` : text;
  return {
    keywords: keywords.length > 40 ? `${keywords.slice(0, 40)}…` : keywords,
    scope,
    reply: replySnippet,
    button: reply.button_title,
    platforms: filters.platforms,
  };
};
